/**
 * THE PRODUCTION CALLER. A player-facing session a UI can drive, with both of this
 * lane's verbs on it and the chosen destination written into the save.
 *
 * -- WHY THIS FILE EXISTS -----------------------------------------------------
 *
 * CL2-R2-A, high, and the largest gap on this lane after two rounds: NOTHING IN THIS LANE
 * HAD A PLAYER. The critic's census over the whole source tree found `chartView`,
 * `exitAvailability`, `resolveVoyage`, `orderSailTo`, `orderReturnToTrack`,
 * `serializeOrder` and sim-core's own `openSession` with ZERO production references -
 * every caller was a test. So the suite proved properties over a state space no execution
 * path could enter. CL1-R1-02's invariant, "for every reachable state s: s is the
 * natural-drift state XOR the view at s offers a working exit", held over 690 states that
 * no browser reached, while the states a browser did reach contained no boat at all.
 *
 * The class is self-concealing, which is what makes it worth a file rather than a patch:
 * adding tests raises the pass count and lowers the chance anyone notices there is no
 * caller, and neither `tsc` nor `vitest` nor a mutation probe can see it, because all
 * three operate inside the island. sim-core learned the same lesson one round earlier -
 * `src/sim/persistence.ts`'s own header says every round-1 save defect came from "a save
 * surface with NO PRODUCTION CALLER", because "an unused surface has no wrong answers, it
 * never has to pick one". This file picks, for the voyage layer.
 *
 * -- WHAT A UI HAS TO DO, IN FULL ---------------------------------------------
 *
 *     const session = openVoyageSession({ storage, nowMs, seed });
 *     const view = session.tick(nowMs);        // every frame, from the host's clock
 *     view.chart.discovered                    // rows, nearest first, with ETAs
 *     view.chart.helm.exit                     // the exit control, present in every state
 *     session.sailTo(placeId, nowMs);          // VERB 1 - sail to a charted place
 *     session.returnToTrack(nowMs);            // VERB 2 - give the boat back to the sea
 *     session.exportText();                    // the save, as one string, order included
 *
 * That is the whole API. There is no other order of calls to get wrong: `tick` is
 * idempotent in its argument, both verbs are total and refuse rather than throw, and the
 * save is written by the session rather than by the caller remembering to.
 *
 * -- THE THREE THINGS IT REFUSES TO LET A CALLER GET WRONG ---------------------
 *
 *   1. THE WORLD COMES FROM THE SAVE. `simVoyageWorldFor(state)` builds the world from
 *      the save's own seed and skill, so the two arguments of `assertSourceMatchesWorld`
 *      are produced by ONE input. CL2-R2-F: round 2's `simVoyageWorld()` baked seed 1
 *      while the progress source read the seed from the save, so the guard threw on the
 *      first frame of any real save. There is no way to name a world here except by
 *      naming the save.
 *   2. THE ORDER IS PERSISTED. Every write carries it - see persistence.ts - and both
 *      verbs force a write immediately, because a destination the player watched
 *      themselves choose is exactly the thing that must not be lost to a closed tab.
 *   3. PRESENCE CHANGES NOTHING. `view(nowMs)` never advances anything and `tick(nowMs)`
 *      lands on the same fix as a `view(nowMs)` that was never ticked at all, because both
 *      settle from timestamps through the same production `advance`. session.test.ts
 *      asserts that with a never-ticking controller against an always-ticking one over the
 *      same seed, which is the form CONTRACT.md section 6 states it in.
 *
 *      ROUND 5 SAID THAT WHILE IT WAS FALSE PAST THE HOLD. The assertion ran six hours,
 *      inside the hold, where nothing is skipped and the defect cannot show. A settled
 *      save viewed 100 hours after its last tick and then ticked at the same instant put
 *      the boat 384 leagues from itself (seed 4242; the round 5 critic measured 724 on
 *      another), because the round's clock added the current absence's paid share to a
 *      cursor that is elapsed time - see anchorPaidTicks in sim-bridge.ts. The same
 *      assertion now also runs on a settled save across an absence past the hold and at
 *      cadences that are not multiples of it, in session.test.ts and paid-clock.test.ts.
 *
 * -- WHAT IT IS NOT -----------------------------------------------------------
 *
 * NO HOST. No clock, no storage implementation, no rendering, no input handling. Time
 * arrives as `nowMs` and storage arrives as a port, exactly as sim-core's session does, so
 * this runs unchanged in a test, a worker or a headless harness. The browser adapter is
 * ten lines and lives outside this directory, in `src/app/voyage-entry.ts`, because
 * no-steering.test.ts forbids this directory from naming a host at all.
 *
 * NO STEERING. The only two things a player may do to the boat are the two verbs above.
 * There is no heading, no rudder and no held input on this interface or under it.
 */

import type { ChartView } from './chart-view';
import { chartView } from './chart-view';
import { orderFromEnvelope, voyageOrderStorage, withVoyageOrder } from './persistence';
import type {
  ImportResult,
  SaveEnvelope,
  SaveOutcome,
  SaveStorage,
  Session,
  SimState,
} from './sim-bridge';
import { OFFLINE_HOLD_HOURS, TICK_MS, openSession, previewAbsence } from './sim-bridge';
import { simProgressSource, simVoyageWorldFor, type SimProgressSource } from './sim-bridge';
import type { Millis } from './units';
import type { SailResult, VoyageFix, VoyageOrder } from './voyage';
import { DRIFT_ORDER, orderReturnToTrack, orderSailTo, resolveVoyage } from './voyage';
import type { VoyageWorld } from './world';

export interface VoyageSessionOptions {
  /** Where the save lives. A browser adapter, a Map in a test, whatever a worker has. */
  readonly storage: SaveStorage;
  /** The host's clock at the moment of opening, read once by the host and handed in. */
  readonly nowMs: Millis;
  /** Seed for a fresh run, when there is nothing stored. */
  readonly seed: number;
  readonly key?: string;
  readonly autosaveIntervalMs?: number;
}

/**
 * Everything a surface needs to draw one frame, derived from one timestamp.
 *
 * It is a value and not a set of getters so that a renderer cannot accidentally read two
 * different instants inside one frame - which is the shape of every "the boat jumped"
 * bug this lane could otherwise have.
 */
/**
 * THE HOLD, DISCLOSED TO WHOEVER IS DRAWING THE SCREEN.
 *
 * CONTRACT.md section 6: "The hold is 24 hours... Time beyond it is SKIPPED AND DISCLOSED,
 * never paid at a reduced rate, which is what makes the invariant literally true rather
 * than approximately so."
 *
 * Round 3 held up its end of that sentence everywhere except the last word. sim-core
 * computed the skip in `splitAbsence`, persisted it as an earned field, and the dev
 * harness printed it under the comment "skippedTicks is the honesty number" - while
 * `VoyageView`, the surface this lane published for a UI to bind to, had no field for it
 * and `src/voyage` never read it (CL3-C-04). A disclosure that exists only in code the
 * player cannot reach is not a disclosure.
 *
 * -- WHY IT IS COMPUTED THE WAY IT IS ----------------------------------------
 *
 * `skippedMs` is the save's BANKED skip plus the skip the CURRENT gap would incur. That
 * second term is what makes it presence-invariant: a session that has never ticked and a
 * session ticked sixty times a second report the identical figure for the identical
 * `nowMs`, because ticking moves time out of the pending term and into the banked one and
 * changes nothing else. session.test.ts asserts exactly that, and asserts this equals
 * sim-core's own `advance(state, nowMs).skippedTicks` - a different implementation, which
 * is what makes it a check rather than a restatement.
 */
export interface HoldView {
  /** The hold itself, in hours. The owner's 24, from sim-core's constant. */
  readonly holdHours: number;
  /** Milliseconds of this save's lifetime that fell past the hold and were skipped. */
  readonly skippedMs: number;
  /** The same figure in hours, which is the unit a player would be told it in. */
  readonly skippedHours: number;
  /** Milliseconds the CURRENT absence would skip if it were settled at `nowMs`. */
  readonly pendingSkippedMs: number;
  /** True once anything at all has been skipped. The one bit a HUD needs to decide to speak. */
  readonly hasSkipped: boolean;
  /**
   * Milliseconds of this save's lifetime that WERE paid, at `nowMs` - the round's clock,
   * published beside the number it has to agree with. `paidMs + skippedMs` is the elapsed
   * wall-clock since the save's epoch, to the tick, at every cadence; paid-clock.test.ts
   * asserts it. Round 5 published only the skip and paced the boat off elapsed time, so
   * the disclosure and the boat told opposite stories with no surface on which to notice.
   */
  readonly paidMs: number;
  /** The same figure in hours. */
  readonly paidHours: number;
}

export interface VoyageView {
  readonly nowMs: Millis;
  readonly fix: VoyageFix;
  readonly chart: ChartView;
  /** The order in force. Persisted; see persistence.ts. */
  readonly order: VoyageOrder;
  /** What the hold has skipped, so a surface can say so. Never null. */
  readonly hold: HoldView;
}

/**
 * THERE IS DELIBERATELY NO `state` ON THIS VIEW, and the reason is the whole point of the
 * lane. The first draft carried one, and session.test.ts's never-ticking-versus-always-
 * ticking controller failed on it inside a minute: a `view()` that has never been ticked
 * holds an ANCHOR state, so a HUD reading `view.state.xp` would show 0 coins and 0 catches
 * to a player who had been away for six hours and had in fact earned 43,503 coins. The
 * boat was in the right place and the numbers beside it were a lie.
 *
 * That is presence changing something, arriving through a convenience field. So the field
 * is gone rather than documented around: `view()` answers questions about the BOAT and
 * advances nothing, and any surface that wants earned quantities calls `tick(nowMs)`,
 * which settles the save from the timestamp first. A UI does that every frame anyway.
 */

export interface VoyageSession {
  /** What opening the save had to do. Empty means it opened clean. */
  readonly openNotes: readonly string[];
  /** The world this save sails, built from the save's own progression identity. */
  world(): VoyageWorld;
  /** The simulation state AS OF THE LAST TICK. Settle it by calling `tick` first. */
  state(): SimState;
  envelope(): SaveEnvelope;
  order(): VoyageOrder;

  /**
   * The whole frame, WITHOUT advancing anything. Pure in `nowMs`: same argument, same
   * answer, however many times it is called and whoever is watching.
   */
  view(nowMs: Millis): VoyageView;

  /**
   * Advance the simulation to `nowMs`, autosave if due, and return the frame.
   *
   * A focused tab calls it sixty times a second, a background tab once a minute, a
   * reopened tab once after two days. All three land on the same state, because the
   * simulation settles from the timestamp and never from the number of calls.
   */
  tick(nowMs: Millis): VoyageView;

  /** VERB 1. Sail to an already-discovered place. Refuses; never throws. */
  sailTo(placeId: string, nowMs: Millis): SailResult;

  /** VERB 2. The one control that gives the boat back to its natural progression. */
  returnToTrack(nowMs: Millis): VoyageOrder;

  /**
   * VERB 3, AND THE ONE CONTRACT.md SECTION 6 CALLS "THE PLAYER'S ONE VERB: choose what to
   * fish for" (owner ruling, 2026-09-01). Both NAVIGATION verbs have been on this interface
   * since round 2; the verb the contract actually names was the only one missing, which is
   * the shape of hole worth stating rather than quietly filling: a reader who found `sailTo`
   * and `returnToTrack` here and no target verb would reasonably conclude the target was not
   * a player-facing thing, and section 6 says it is the only one that is.
   *
   * The renderer's chart control shipped the choice anyway, going the long way round the
   * published API - export the envelope with the field spliced in, then `importText` it back -
   * which rewrote the whole save on every selection. That fallback is deleted; this is what
   * replaced it.
   *
   * IT IS A PASS-THROUGH AND DELIBERATELY NOTHING MORE. `Session.setTarget` in
   * src/sim/persistence.ts already puts the id through the SAVE'S OWN validator (`strField`,
   * bounded by `MAX_ID_LENGTH`, control characters rejected) and already writes immediately,
   * because a choice the player is watching the screen make is worse to lose than progress.
   * That file's header records SIM-D6 as the cost of a second validator for this one field:
   * two validators for one field is one validator that can drift. So there is no check here.
   *
   * WHAT IT DOES NOT DO, stated because the name suggests otherwise: it does not reject an id
   * this build does not ship. A clean, short, unknown id IS written to the save; `resolveSpecies`
   * in src/sim/step.ts then ignores it and fishes the best unlocked species, exactly as it does
   * for `''`, which is the value meaning "the best I have unlocked". The catalogue check lives
   * at the surface that offers the rows, not here.
   */
  setTarget(speciesId: string, nowMs: Millis): void;

  /** The save as one string the player can copy anywhere, order included. */
  exportText(): string;
  /** Replace this run with a pasted string. Never throws. */
  importText(text: unknown, nowMs: Millis): ImportResult;
  /** Force a write now, regardless of the autosave interval. */
  save(nowMs: Millis): SaveOutcome;
  lastSavedMs(): number;
}

/**
 * OPEN A SESSION. The single entry point an app calls at startup.
 *
 * The order of operations here is the part worth reading: the storage port is wrapped
 * BEFORE sim-core's session is opened, so the very first autosave - which sim-core writes
 * on the first tick - already carries the boat's order. A session that only started
 * persisting after the first deliberate act would lose a destination chosen in the first
 * fifteen seconds of play.
 */
export function openVoyageSession(options: VoyageSessionOptions): VoyageSession {
  let order: VoyageOrder = DRIFT_ORDER;

  const sim: Session = openSession({
    storage: voyageOrderStorage(options.storage, () => order),
    nowMs: options.nowMs,
    seed: options.seed,
    ...(options.key === undefined ? {} : { key: options.key }),
    ...(options.autosaveIntervalMs === undefined
      ? {}
      : { autosaveIntervalMs: options.autosaveIntervalMs }),
  });

  order = orderFromEnvelope(sim.envelope());

  // The world and the progress source are both derived from the save, and both are
  // rebuilt when the save's identity changes - which happens exactly once, on an import.
  // Caching them on the STATE OBJECT rather than on a flag means a state replaced by any
  // route at all invalidates them; there is no way to forget to call an invalidator.
  let anchor: SimState = sim.state();
  /**
   * THE STATE THIS SESSION OPENED WITH, and the reason `view(t)` is pure in `t`.
   *
   * Round 9's finding: the source was anchored on the LATEST ticked state, and answered
   * every instant at or before that cursor with the cursor. So `view(T)` moved as the tab
   * ran, and a reloaded tab answered for 11:00 with the boat it had at 12:00 - one save,
   * one seed, one instant, four positions across four measured browser loads.
   *
   * Handing this state to the source as its rewind base is what lets an instant between
   * the open and now be REPLAYED instead of clamped. It is read once, never advanced and
   * never written; the object identity is stable, which is what makes the source's memo
   * of it survive the rebuild on every tick. It is replaced on an import and nowhere else,
   * because an import is a different run and this one's past no longer belongs to it.
   */
  let opened: SimState = anchor;
  let world: VoyageWorld = simVoyageWorldFor(anchor);
  let source: SimProgressSource = simProgressSource(anchor, { rewindBase: opened });

  function refresh(): void {
    const current = sim.state();
    if (current === anchor) return;
    const seedChanged =
      current.seed !== anchor.seed ||
      current.skill !== anchor.skill ||
      current.targetSpecies !== anchor.targetSpecies;
    anchor = current;
    // The source is anchored at a state, so it is rebuilt on every state change. The
    // world is solved against a CURVE, so it only moves when the curve's identity does -
    // and simVoyageWorldFor memoises by exactly that identity.
    source = simProgressSource(anchor, { rewindBase: opened });
    if (seedChanged) world = simVoyageWorldFor(anchor);
  }

  /**
   * The banked skip plus the pending one, read off the ANCHOR state rather than off a
   * ticked one. Pure in `nowMs`, exactly like the progress source beside it.
   */
  function holdView(nowMs: Millis): HoldView {
    const state = sim.state();
    const pendingTicks = previewAbsence(state, nowMs).skippedTicks;
    const skippedMs = (state.skippedTicks + pendingTicks) * TICK_MS;
    // Off the SAME source the fix is resolved from, so the clock a HUD prints and the
    // clock the boat sails are one number rather than two implementations.
    const paidMs = source.paidTicksAt(nowMs) * TICK_MS;
    return {
      holdHours: OFFLINE_HOLD_HOURS,
      skippedMs,
      skippedHours: skippedMs / 3_600_000,
      pendingSkippedMs: pendingTicks * TICK_MS,
      hasSkipped: skippedMs > 0,
      paidMs,
      paidHours: paidMs / 3_600_000,
    };
  }

  function frame(nowMs: Millis): VoyageView {
    const fix = resolveVoyage(world, order, nowMs, source);
    return { nowMs, fix, chart: chartView(world, fix), order, hold: holdView(nowMs) };
  }

  /** A deliberate player act is written at once, not at the next interval. */
  function commit(next: VoyageOrder, nowMs: Millis): void {
    order = next;
    sim.save(nowMs);
  }

  return {
    openNotes: sim.openNotes,
    world: () => world,
    state: () => sim.state(),
    envelope: () => sim.envelope(),
    order: () => order,

    view(nowMs: Millis): VoyageView {
      refresh();
      return frame(nowMs);
    },

    tick(nowMs: Millis): VoyageView {
      sim.tick(nowMs);
      refresh();
      return frame(nowMs);
    },

    sailTo(placeId: string, nowMs: Millis): SailResult {
      refresh();
      const result = orderSailTo(world, order, nowMs, source, placeId);
      if (result.ok) commit(result.order, nowMs);
      return result;
    },

    returnToTrack(nowMs: Millis): VoyageOrder {
      // A new course starts a new absence window before its future terminal is projected.
      sim.tick(nowMs);
      refresh();
      commit(orderReturnToTrack(world, order, nowMs, source), nowMs);
      return order;
    },

    setTarget(speciesId: string, nowMs: Millis): void {
      sim.setTarget(speciesId, nowMs);
      // The world and the progress source are cached against the state OBJECT, and
      // `refresh` counts `targetSpecies` as part of the identity that invalidates them.
      // Without this line a caller that read `world()` straight after choosing - the one
      // reader that does not refresh on its own - would get the world built for the target
      // it just replaced. Nothing else here: the write already happened, in sim-core.
      refresh();
    },

    // The exported string goes through the same splice the storage port uses, so the
    // string a player copies and the string on disk are the same string.
    exportText: () => withVoyageOrder(sim.exportText(), order),

    importText(text: unknown, nowMs: Millis): ImportResult {
      const result = sim.importText(text, nowMs);
      if (!result.ok) return result;
      // Read the order back off the imported envelope rather than out of the text: the
      // normaliser has already decided what survived, and a second parse here could
      // disagree with it.
      order = orderFromEnvelope(sim.envelope());
      // A DIFFERENT RUN, so the old open-state is no longer a base this session may
      // replay from: its cursor and its ledger belong to a save that is gone.
      opened = sim.state();
      refresh();
      return result;
    },

    save: (nowMs: Millis) => sim.save(nowMs),
    lastSavedMs: () => sim.lastSavedMs(),
  };
}
